import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'expo-router'
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Animated,
  Platform,
  useWindowDimensions,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import {
  Sparkles,
  Palette,
  Users,
  Settings,
  X,
  Home,
  Building2,
  Mail,
  Package,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Lock,
} from 'lucide-react-native'
import type { LucideIcon } from 'lucide-react-native'
import { BaseModal } from '../../../../components/ui/BaseModal/BaseModal'
import {
  getDrawerWrapperPosition,
  getModalContentAlignment,
} from '../../../../components/layout/helpers/getModalContentPosition'
import { useTheme } from '../../../theme/useTheme'
import { ENTERPRISE_CONTACT_PATH } from '../../../packs/constants/enterpriseContact.constants'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { SoundPressable } from '../../../shared/components/SoundPressable/SoundPressable'
import { SOUND_IDS } from '../../../shared/constants/sounds.constants'
import { soundService } from '../../../shared/services/SoundService'
import { useSoundStore } from '../../../shared/stores/soundStore'
import {
  ACCOUNT_ADMIN_NAV,
  type AccountAdminNavId,
} from '../../constants/accountAdmin.constants'
import { ACCOUNT_ADMIN_DRAWER_SLIDE_MS } from '../../constants/accountAdminNav.constants'
import {
  getAccountAdminDrawerWidthPx,
  getAccountAdminNavItemsForPlatform,
  getAccountAdminNavLabel,
  isAccountAdminDrawerOverlayMode,
  isAccountAdminOrgNavSurfacePath,
} from '../../helpers/accountAdminNav.helper'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { getAccountAdminNavDrawerStyles } from './AccountAdminNavDrawer.styles'
import type { AccountAdminSiteMenuLink } from '../../types/accountAdminNav.types'

const NAV_ICONS: Record<AccountAdminNavId, LucideIcon> = {
  [ACCOUNT_ADMIN_NAV.START_HERE]: Sparkles,
  [ACCOUNT_ADMIN_NAV.BRANDING]: Palette,
  [ACCOUNT_ADMIN_NAV.MEMBERS]: Users,
  [ACCOUNT_ADMIN_NAV.SETTINGS]: Settings,
  [ACCOUNT_ADMIN_NAV.PLAN_BILLING]: Package,
  [ACCOUNT_ADMIN_NAV.INVOICE_DETAILS]: CreditCard,
}

const ICON_SIZE = 22
const ICON_STROKE = 2.25

const SITE_LINK_ICONS: Record<string, LucideIcon> = {
  home: Home,
  organizations: Building2,
  contact: Mail,
  packs: Package,
}

const BILLING_LOCKED_NAV_IDS: ReadonlySet<AccountAdminNavId> = new Set([
  ACCOUNT_ADMIN_NAV.BRANDING,
  ACCOUNT_ADMIN_NAV.MEMBERS,
  ACCOUNT_ADMIN_NAV.SETTINGS,
])

const LOCK_ICON_SIZE = 14

interface AccountAdminNavDrawerProps {
  visible: boolean
  activeNav: AccountAdminNavId
  onDismiss: () => void
  onSelectNav: (id: AccountAdminNavId) => void
  /** Marketing routes (Home, Organizations, …) when the top bar hides primary links. */
  siteMenuLinks?: AccountAdminSiteMenuLink[]
  /**
   * When false, "Organizations" is a normal link (e.g. `/organizations`); org-admin sub-rows stay hidden.
   * @default true
   */
  signedIn?: boolean
  /** When false, Branding / Members / Settings show a lock and route to Plan & Billing. */
  billingUnlocked?: boolean
}

export function AccountAdminNavDrawer({
  visible,
  activeNav,
  onDismiss,
  onSelectNav,
  siteMenuLinks,
  signedIn = true,
  billingUnlocked = true,
}: AccountAdminNavDrawerProps) {
  const copy = useAccountAdminCopy()
  const theme = useTheme()
  const pathname = usePathname() ?? ''
  const isAccountAdminSurface = isAccountAdminOrgNavSurfacePath(pathname)
  const isMarketingHomePath = pathname === '/' || pathname === ''
  const isPacksPath = pathname === '/packs' || pathname.startsWith('/packs/')
  const isContactPath =
    pathname === '/contact' ||
    pathname.startsWith('/contact/') ||
    pathname === ENTERPRISE_CONTACT_PATH ||
    pathname.startsWith(`${ENTERPRISE_CONTACT_PATH}/`)
  const insets = useSafeAreaInsets()
  const { width: windowWidth } = useWindowDimensions()
  const drawerWidthPx = getAccountAdminDrawerWidthPx(windowWidth)
  const closeDrawerAfterNavSelection = useMemo(
    () => Platform.OS !== 'web' || isAccountAdminDrawerOverlayMode(windowWidth),
    [windowWidth],
  )

  const styles = useMemo(
    () => getAccountAdminNavDrawerStyles(theme, insets.top),
    [theme, insets.top],
  )

  const [isClosing, setIsClosing] = useState(false)
  const [orgExpanded, setOrgExpanded] = useState(false)
  const marketingDrawerSwishPlayedRef = useRef(false)
  const slideAnim = useRef(new Animated.Value(-drawerWidthPx)).current
  const drawerWidthRef = useRef(drawerWidthPx)
  drawerWidthRef.current = drawerWidthPx

  const showModal = visible || isClosing

  useEffect(() => {
    if (!visible && !isClosing) {
      slideAnim.setValue(-drawerWidthPx)
    }
  }, [visible, isClosing, drawerWidthPx, slideAnim])

  useEffect(() => {
    if (visible && !isClosing) {
      slideAnim.setValue(-drawerWidthPx)
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: ACCOUNT_ADMIN_DRAWER_SLIDE_MS,
        useNativeDriver: Platform.OS !== 'web',
      }).start()
    }
  }, [visible, isClosing, drawerWidthPx, slideAnim])

  useEffect(() => {
    if (visible && !isClosing) {
      if (!marketingDrawerSwishPlayedRef.current) {
        marketingDrawerSwishPlayedRef.current = true
        void soundService.warmFromUserGesture()
        useSoundStore.getState().playSound(SOUND_IDS.UI_MENU_OPEN_SWISH)
      }
    } else if (!visible) {
      marketingDrawerSwishPlayedRef.current = false
    }
  }, [visible, isClosing])

  useEffect(() => {
    if (visible && !isClosing) {
      setOrgExpanded(isAccountAdminOrgNavSurfacePath(pathname))
    }
  }, [visible, isClosing, pathname])

  const finishClose = useCallback(() => {
    setIsClosing(false)
    onDismiss()
  }, [onDismiss])

  const handleDismiss = useCallback(() => {
    if (isClosing) return
    setIsClosing(true)
    const w = drawerWidthRef.current
    Animated.timing(slideAnim, {
      toValue: -w,
      duration: ACCOUNT_ADMIN_DRAWER_SLIDE_MS,
      useNativeDriver: Platform.OS !== 'web',
    }).start(({ finished }) => {
      if (finished) {
        finishClose()
      }
    })
  }, [isClosing, slideAnim, finishClose])

  const handleSelect = useCallback(
    (id: AccountAdminNavId) => {
      onSelectNav(id)
      if (closeDrawerAfterNavSelection) {
        handleDismiss()
      }
    },
    [onSelectNav, closeDrawerAfterNavSelection, handleDismiss],
  )

  const handleSiteLink = useCallback(
    (onPress: () => void) => {
      onPress()
      if (closeDrawerAfterNavSelection) {
        handleDismiss()
      }
    },
    [closeDrawerAfterNavSelection, handleDismiss],
  )

  const orgParentLabel =
    siteMenuLinks?.find((l) => l.key === 'organizations')?.label ??
    copy.ORG_DRAWER_PARENT_FALLBACK

  const orgNavDrawerItems = useMemo(() => getAccountAdminNavItemsForPlatform(), [])

  const renderOrgAdminRows = () => (
    <>
      {orgNavDrawerItems.map((id) => {
        const isLocked = !billingUnlocked && BILLING_LOCKED_NAV_IDS.has(id)
        const selected = isAccountAdminSurface && activeNav === id
        const Icon = NAV_ICONS[id]
        const label = getAccountAdminNavLabel(id, copy)
        const handleLockedOrNormal = () =>
          isLocked ? handleSelect(ACCOUNT_ADMIN_NAV.PLAN_BILLING) : handleSelect(id)
        return (
          <Pressable
            key={id}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={isLocked ? `${label}${copy.navLockedA11ySuffix}` : label}
            onPress={handleLockedOrNormal}
            style={({ pressed, hovered }) => [
              styles.navRow,
              styles.navRowSubLevel,
              selected && styles.navRowActive,
              isLocked && styles.navRowLocked,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.navRowPressed,
              hovered && Platform.OS === 'web' && styles.navRowHover,
            ]}
          >
            <Icon
              size={ICON_SIZE}
              color={isLocked ? theme.gray[400] : selected ? theme.primaryLight : theme.gray[600]}
              strokeWidth={ICON_STROKE}
            />
            <Text
              style={[
                styles.navLabel,
                selected && styles.navLabelActive,
                isLocked && styles.navLabelLocked,
              ]}
            >
              {label}
            </Text>
            {isLocked ? (
              <Lock size={LOCK_ICON_SIZE} color={theme.gray[400]} strokeWidth={2} />
            ) : null}
          </Pressable>
        )
      })}
    </>
  )

  return (
    <BaseModal
      isVisible={showModal}
      onDismiss={handleDismiss}
      dismissible={false}
      blocksBackgroundInteraction={false}
      contentAlignment={getModalContentAlignment()}
      contentPosition={getDrawerWrapperPosition()}
      contentContainerStyle={styles.drawerWrapper}
      testID="account-admin-nav-drawer"
      contentTestID="account-admin-nav-drawer-content"
      animationType="fade"
    >
      <Animated.View
        style={[
          styles.drawerPanel,
          { width: drawerWidthPx, transform: [{ translateX: slideAnim }] },
        ]}
        pointerEvents="auto"
      >
        <View style={styles.drawerInner}>
          <View style={styles.drawerHeader}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close menu"
              onPress={handleDismiss}
              style={({ pressed, hovered }) => [
                styles.closePressable,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.closePressed,
                hovered && Platform.OS === 'web' && styles.closeHover,
              ]}
            >
              <X size={22} color={theme.gray[700]} strokeWidth={ICON_STROKE} />
            </Pressable>
          </View>
          <ScrollView
            style={styles.navScroll}
            contentContainerStyle={styles.navScrollContent}
            showsVerticalScrollIndicator={false}
          >
            {siteMenuLinks && siteMenuLinks.length > 0 ? (
              siteMenuLinks.map((item) => {
                if (item.key === 'organizations' && signedIn) {
                  const Chevron = orgExpanded ? ChevronDown : ChevronRight
                  const orgParentSelected = !orgExpanded && isAccountAdminSurface
                  return (
                    <React.Fragment key={item.key}>
                      <SoundPressable
                        tapSoundId={
                          orgExpanded
                            ? SOUND_IDS.UI_SECTION_COLLAPSE
                            : SOUND_IDS.UI_SECTION_EXPAND
                        }
                        accessibilityRole="button"
                        accessibilityState={{ expanded: orgExpanded }}
                        accessibilityLabel={orgParentLabel}
                        accessibilityHint={
                          orgExpanded
                            ? 'Collapse organization setup'
                            : 'Expand organization setup'
                        }
                        onPress={() => setOrgExpanded((open) => !open)}
                        style={({ pressed, hovered }) => [
                          styles.navRow,
                          orgParentSelected && styles.navRowOrgParentActive,
                          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                          pressed && styles.navRowPressed,
                          hovered && Platform.OS === 'web' && styles.navRowHover,
                        ]}
                      >
                        <Building2
                          size={ICON_SIZE}
                          color={orgParentSelected ? theme.primaryLight : theme.gray[600]}
                          strokeWidth={ICON_STROKE}
                        />
                        <Text style={[styles.navLabel, orgParentSelected && styles.navLabelActive]}>
                          {item.label}
                        </Text>
                        <Chevron
                          size={20}
                          color={orgParentSelected ? theme.primaryLight : theme.gray[600]}
                          strokeWidth={ICON_STROKE}
                        />
                      </SoundPressable>
                      {orgExpanded ? renderOrgAdminRows() : null}
                    </React.Fragment>
                  )
                }
                const SiteIcon = SITE_LINK_ICONS[item.key] ?? Mail
                const siteLinkSelected =
                  (item.key === 'home' && isMarketingHomePath) ||
                  (item.key === 'packs' && isPacksPath) ||
                  (item.key === 'contact' && isContactPath)
                return (
                  <Pressable
                    key={item.key}
                    accessibilityRole="link"
                    accessibilityState={{ selected: siteLinkSelected }}
                    accessibilityLabel={item.label}
                    onPress={() => handleSiteLink(item.onPress)}
                    style={({ pressed, hovered }) => [
                      styles.navRow,
                      siteLinkSelected && styles.navRowActive,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.navRowPressed,
                      hovered && Platform.OS === 'web' && styles.navRowHover,
                    ]}
                  >
                    <SiteIcon
                      size={ICON_SIZE}
                      color={siteLinkSelected ? theme.primaryLight : theme.gray[600]}
                      strokeWidth={ICON_STROKE}
                    />
                    <Text
                      style={[
                        styles.navLabel,
                        siteLinkSelected && styles.navLabelActive,
                      ]}
                    >
                      {item.label}
                    </Text>
                  </Pressable>
                )
              })
            ) : (
              <>
                <SoundPressable
                  tapSoundId={
                    orgExpanded
                      ? SOUND_IDS.UI_SECTION_COLLAPSE
                      : SOUND_IDS.UI_SECTION_EXPAND
                  }
                  accessibilityRole="button"
                  accessibilityState={{ expanded: orgExpanded }}
                  accessibilityLabel={orgParentLabel}
                  accessibilityHint={
                    orgExpanded
                      ? 'Collapse organization setup'
                      : 'Expand organization setup'
                  }
                  onPress={() => setOrgExpanded((open) => !open)}
                  style={({ pressed, hovered }) => [
                    styles.navRow,
                    !orgExpanded &&
                      isAccountAdminSurface &&
                      styles.navRowOrgParentActive,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.navRowPressed,
                    hovered && Platform.OS === 'web' && styles.navRowHover,
                  ]}
                >
                  <Building2
                    size={ICON_SIZE}
                    color={!orgExpanded && isAccountAdminSurface ? theme.primaryLight : theme.gray[600]}
                    strokeWidth={ICON_STROKE}
                  />
                  <Text
                    style={[
                      styles.navLabel,
                      !orgExpanded && isAccountAdminSurface && styles.navLabelActive,
                    ]}
                  >
                    {orgParentLabel}
                  </Text>
                  {orgExpanded ? (
                    <ChevronDown
                      size={20}
                      color={theme.gray[600]}
                      strokeWidth={ICON_STROKE}
                    />
                  ) : (
                    <ChevronRight
                      size={20}
                      color={isAccountAdminSurface ? theme.primaryLight : theme.gray[600]}
                      strokeWidth={ICON_STROKE}
                    />
                  )}
                </SoundPressable>
                {orgExpanded ? renderOrgAdminRows() : null}
              </>
            )}
          </ScrollView>
        </View>
      </Animated.View>
    </BaseModal>
  )
}
